// Stat tiles for the recording map's sidebar tabs. Formatting and translation are passed
// in so this stays free of the measurement-preference/i18n singletons and Jest can run it.

import {
  elevationLossFromAltitudes,
  elevationLossFromCoordinates,
} from '../../../utils/elevation/elevationTotals';
import {
  durationMinutesForActiveView,
  elevationGainMetersForActiveView,
  plannedTrailsForActiveView,
} from '../../../utils/elevation/plannedTrailStats';

export interface StatItem {
  key: string;
  label: string;
  value: string;
}

export interface StatFormatters {
  distance: (meters: number) => string;
  elevation: (meters: number) => string;
  duration: (seconds: number) => string;
  t: (key: string, params?: Record<string, unknown>) => string;
}

export interface RecordingStatsInput {
  distanceMeters?: number | null;
  elapsedSeconds?: number | null;
  elevationGainMeters?: number | null;
  altitudes?: Array<number | null | undefined> | null;
}

export function recordingStatItems(recording: RecordingStatsInput, f: StatFormatters): StatItem[] {
  return [
    { key: 'distance', label: f.t('recording_map.stats.distance'), value: f.distance(recording.distanceMeters || 0) },
    { key: 'time', label: f.t('recording_map.stats.time'), value: f.duration(recording.elapsedSeconds || 0) },
    { key: 'ascent', label: f.t('recording_map.stats.ascent'), value: f.elevation(recording.elevationGainMeters || 0) },
    // Recordings don't store a descent figure; derive it from the altitude series the same
    // way the elevation panel does, so the two agree.
    {
      key: 'descent',
      label: f.t('recording_map.stats.descent'),
      value: f.elevation(elevationLossFromAltitudes(recording.altitudes || [])),
    },
  ];
}

export interface HikeStatsInput {
  distanceMeters?: number | null;
  durationMinutes?: number | null;
  elevationGainMeters?: number | null;
  elevationLossMeters?: number | null;
  hikeDays?: unknown[] | null;
  plannedTrails?: any[] | null;
  /** The hike's route GeoJSON features (MultiLineStrings carrying `routeStyle`). */
  routeFeatures?: any[] | null;
}

/**
 * The hike's totals, taken from the same sources as the day panel and elevation profile
 * beside them so the three agree: distance, ascent and time summed over the main routes
 * (alternatives, spurs and transport excluded), and descent from those routes' geometry.
 * The hike's own stored totals can lag behind edits to its routes, so they are only a
 * fallback for a hike whose routes haven't loaded.
 */
function hikeTotals(hike: HikeStatsInput) {
  const full = { type: 'full' };
  const trails = hike.plannedTrails || [];
  const main = plannedTrailsForActiveView(trails, full);
  if (main.length === 0) {
    return {
      distance: hike.distanceMeters || 0,
      ascent: hike.elevationGainMeters || 0,
      descent: hike.elevationLossMeters || 0,
      minutes: hike.durationMinutes || 0,
    };
  }
  const distance = main.reduce(
    (sum, trail) => sum + (Number((trail?.ptv2PlannedTrail || trail)?.distanceMeters) || 0),
    0,
  );
  const mainLines = (hike.routeFeatures || []).filter((feature) =>
    feature?.geometry?.type === 'MultiLineString'
      && (!feature.properties?.routeStyle || feature.properties.routeStyle === 'route'));
  const descent = mainLines.length > 0
    ? mainLines.reduce((sum, feature) => sum + feature.geometry.coordinates
      .reduce((lineSum: number, line: number[][]) => lineSum + elevationLossFromCoordinates(line), 0), 0)
    : hike.elevationLossMeters || 0;
  return {
    distance,
    ascent: elevationGainMetersForActiveView(trails, full) ?? hike.elevationGainMeters ?? 0,
    descent: Math.round(descent),
    minutes: durationMinutesForActiveView(trails, full) || hike.durationMinutes || 0,
  };
}

/**
 * A multi-day hike shows how many days it runs over where a single-day one shows its
 * estimated walking time, which is the more useful second figure for a day hike.
 */
export function hikeStatItems(hike: HikeStatsInput, f: StatFormatters): StatItem[] {
  const totals = hikeTotals(hike);
  const days = hike.hikeDays?.length || 0;
  const second: StatItem = days > 1
    ? { key: 'stages', label: f.t('recording_map.stats.stages'), value: f.t('recording_map.stats.days_count', { count: days }) }
    : { key: 'time', label: f.t('recording_map.stats.est_time'), value: f.duration(totals.minutes * 60) };
  return [
    { key: 'distance', label: f.t('recording_map.stats.distance'), value: f.distance(totals.distance) },
    second,
    { key: 'ascent', label: f.t('recording_map.stats.ascent'), value: f.elevation(totals.ascent) },
    { key: 'descent', label: f.t('recording_map.stats.descent'), value: f.elevation(totals.descent) },
  ];
}
